import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Car, Footprints } from '../../icons';
import { getEventTravelTime, type TravelTimeAvailable } from '../../api/calendarTravel';
import { TravelMap } from './TravelMap';
import { tTravel, formatDuration, formatDistance } from './lib/travelI18n';
import './TravelMap.css';

/** 길이 없을 때도 늘 같은 배열 — 새 `[]` 를 넘기면 부모가 다시 그릴 때마다 지도를 새로 만들어 끌어 둔 자리가 풀린다. */
const NO_PATH: ReadonlyArray<readonly [number, number]> = [];

/**
 * 「외부」 일정 상세의 이동시간 칸 (2026-10-13) — 경로 지도 + 자동차 · 도보(어림).
 *
 * ## 못 셀 때는 «조용히» 종전 그대로
 *
 * 서버가 `available:false`(키 없음·카카오 실패·장소를 못 찾음)를 주거나 요청이 실패하면 이 칸은
 * 아무것도 안 그리고 `fallback`(종전 장소 지도 `EventLocationMap`)을 그린다 — 오류 알림을 띄우지 않는다.
 * 묻는 동안에는 아무것도 그리지 않는다 — 장소 지도를 먼저 그렸다가 경로 지도로 바꾸면 화면이 뛴다.
 */
export function EventTravelTime({ eventId, occurrenceAt, fallback, onSettled }: {
  eventId: string;
  /** 반복 일정 회차를 열었으면 그 회차의 `occurrenceAt` — 회차의 장소로 센다. */
  occurrenceAt?: string | null;
  fallback?: ReactNode;
  /** 물어본 결과를 알린다 — 경로를 그리면 true, `fallback` 으로 물러서면 false(2026-10-30 · 옵셔널). */
  onSettled?: (shown: boolean) => void;
}) {
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'none' } | { kind: 'ok'; data: TravelTimeAvailable }>({ kind: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    getEventTravelTime(eventId, ctrl.signal, occurrenceAt)
      .then((r) => { if (!ctrl.signal.aborted) setState(r.available ? { kind: 'ok', data: r } : { kind: 'none' }); })
      .catch(() => { if (!ctrl.signal.aborted) setState({ kind: 'none' }); });
    return () => ctrl.abort();
  }, [eventId, occurrenceAt]);

  useEffect(() => {
    if (state.kind !== 'loading') settledRef.current?.(state.kind === 'ok');
  }, [state.kind]);

  if (state.kind === 'loading') return null;
  if (state.kind === 'none') return <>{fallback ?? null}</>;

  const { origin, destination, driving, walking } = state.data;
  return (
    <div className="travel-time" data-testid="travel-time">
      <TravelMap origin={origin} destination={destination} path={driving?.path ?? NO_PATH} />
      <ul className="travel-time-rows" aria-label={tTravel('heading')}>
        {driving && (
          <li className="travel-time-row" data-mode="car">
            <Car size={14} aria-hidden />
            <span className="travel-time-mode">{tTravel('car')}</span>
            <strong className="travel-time-value">{formatDuration(driving.durationSeconds)}</strong>
            <span className="travel-time-dist">({formatDistance(driving.distanceMeters)})</span>
          </li>
        )}
        <li className="travel-time-row" data-mode="walk" title={tTravel('walk_note')}>
          <Footprints size={14} aria-hidden />
          <span className="travel-time-mode">{tTravel('walk')}</span>
          <strong className="travel-time-value">{tTravel('approx', { v: formatDuration(walking.durationSeconds) })}</strong>
          <span className="travel-time-dist">({tTravel('approx', { v: formatDistance(walking.distanceMeters) })})</span>
        </li>
      </ul>
      <div className="travel-time-origin">{tTravel('from_worksite', { name: origin.name })}</div>
      {destination.matchedBy === 'keyword' && destination.name && (
        <div className="travel-time-dest" data-testid="travel-time-dest" title={tTravel('to_place_note')}>
          {tTravel('to_place', { name: destination.name })}
          {destination.address ? <span className="travel-time-dest-addr"> · {destination.address}</span> : null}
        </div>
      )}
    </div>
  );
}
